import { Box, Button, Grid, Stack, Text } from '@ltvco/refresh-lib/theme';
import {
  CopyTooltip,
  formatPhone,
  unformatPhone,
  updateAccountInfo,
  useScreenSize,
} from '@ltvco/refresh-lib/utils';
import { enqueueSnackbar, useSession } from '@ltvco/refresh-lib/v1';
import { useQueryClient } from '@ltvco/refresh-lib/vendors';
import { Edit as EditIcon } from '@mui/icons-material';
import { useState } from 'react';

import {
  StyledAccountPageContainer,
  StyledDarkenedStack,
  StyledInitials,
  StyledInput,
} from 'components/DeepSearch/components/AccountPage/sections/components';

export const ProfileDetails = () => {
  const [editMode, setEditMode] = useState(false);
  const {
    session: { account },
  } = useSession();
  const queryClient = useQueryClient();
  const { isMobile } = useScreenSize();
  const [firstName, setFirstName] = useState(
    account?.account?.user_info?.first_name || ''
  );
  const [lastName, setLastName] = useState(
    account?.account?.user_info?.last_name || ''
  );
  const [email, setEmail] = useState(account?.account?.user_info?.email || '');
  const [phoneNumber, setPhoneNumber] = useState(
    account?.account?.user_info?.phone_number || ''
  );
  const [currentPassword, setCurrentPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  /** Valid US phone: 10 digits or 11 digits starting with 1 */
  const isValidPhone = (value: string) => {
    const digits = unformatPhone(value).replace(/\D/g, '');
    if (digits.length === 10) {
      return true;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
      return true;
    }
    return false;
  };

  const validatePhone = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setPhoneError('Please enter a phone number');
      return false;
    }
    if (!isValidPhone(trimmed)) {
      setPhoneError('Please enter a valid phone number');
      return false;
    }
    setPhoneError(null);
    return true;
  };

  const handleResetForm = () => {
    setEditMode(false);
    setFirstName(account?.account?.user_info?.first_name || '');
    setLastName(account?.account?.user_info?.last_name || '');
    setEmail(account?.account?.user_info?.email || '');
    setPhoneNumber(account?.account?.user_info?.phone_number || '');
    setCurrentPassword('');
    setPhoneError(null);
  };

  const handleSaveChanges = async () => {
    if (isSubmitting) {
      return;
    }
    if (!validatePhone(phoneNumber)) {
      return;
    }
    setIsSubmitting(true);
    try {
      await updateAccountInfo({
        account: {
          first_name: firstName,
          last_name: lastName,
          email,
          phone_number: phoneNumber,
        },
        currentPassword,
      });

      await queryClient.invalidateQueries({ queryKey: ['account'] });
      enqueueSnackbar('Changes saved successfully', { variant: 'success' });
      handleResetForm();
    } catch (_) {
      enqueueSnackbar('Failed to save changes, check your current password', {
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <StyledAccountPageContainer spacing={2.5}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Text variant="h3">Profile Details</Text>
        {!editMode && !isMobile && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setEditMode(true)}
          >
            <EditIcon sx={{ fontSize: '16px', marginRight: '8px' }} />
            Update Information
          </Button>
        )}
      </Box>
      <StyledDarkenedStack alignItems="center" padding={2.5} spacing={2.5}>
        <Text fontWeight="bold">When contacting customer care use:</Text>
        <Stack direction="row" alignItems="center" gap={1}>
          <StyledInitials>
            {account?.account?.user_info?.first_name?.[0] || ''}
            {account?.account?.user_info?.last_name?.[0] || ''}
          </StyledInitials>
          <Text>Member ID:</Text>
          <Text fontWeight="bold" color="primary.main">
            {account?.account?.user_info?.user_code}
          </Text>
          <CopyTooltip value={account?.account?.user_info?.user_code || ''} />
        </Stack>
      </StyledDarkenedStack>
      {!editMode ? (
        <Grid
          container
          spacing={2}
          sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}
        >
          <Grid size={6}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Text fontWeight="bold">First Name:</Text>
              <Text>{account?.account?.user_info?.first_name || ''}</Text>
            </Stack>
          </Grid>
          <Grid size={6}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Text fontWeight="bold">Last Name:</Text>
              <Text>{account?.account?.user_info?.last_name || ''}</Text>
            </Stack>
          </Grid>
          <Grid size={6}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Text fontWeight="bold">Email:</Text>
              <Text>{account?.account?.user_info?.email || ''}</Text>
            </Stack>
          </Grid>
          <Grid size={6}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Text fontWeight="bold">Phone Number:</Text>
              <Text>
                {formatPhone(account?.account?.user_info?.phone_number || '')}
              </Text>
            </Stack>
          </Grid>
          {isMobile && (
            <Grid
              size={12}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={() => setEditMode(true)}
              >
                <EditIcon sx={{ fontSize: '16px', marginRight: '8px' }} />
                Update Information
              </Button>
            </Grid>
          )}
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid size={6}>
            <StyledInput
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </Grid>
          <Grid size={6}>
            <StyledInput
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Grid>
          <Grid size={6}>
            <StyledInput
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Grid>
          <Grid size={6}>
            <Stack spacing={0.5}>
              <StyledInput
                label="Phone Number"
                value={formatPhone(phoneNumber)}
                onChange={(e) => {
                  setPhoneNumber(e.target.value);
                  validatePhone(e.target.value);
                }}
                type="tel"
                error={!!phoneError}
                slotProps={
                  phoneError
                    ? {
                        input: {
                          'aria-invalid': true,
                          'aria-describedby': 'phone-error',
                        },
                      }
                    : undefined
                }
              />
              {phoneError && (
                <Text
                  id="phone-error"
                  variant="caption"
                  color="error.main"
                  sx={{ px: 1.5 }}
                >
                  {phoneError}
                </Text>
              )}
            </Stack>
          </Grid>
          <Grid size={12}>
            <StyledInput
              label="Current Password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              type="password"
            />
          </Grid>
        </Grid>
      )}
      {editMode && (
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            color="primary"
            onClick={handleResetForm}
            sx={{ border: 'none' }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveChanges}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </Stack>
      )}
    </StyledAccountPageContainer>
  );
};
